import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DataGrid } from '@mui/x-data-grid';
import Alert from '@mui/material/Alert';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import { useIntegrationLogs, useRetryIntegrationLog } from '../../../hooks/useIntegrations';
import DataGridCard from '../../../components/dashboard/DataGridCard';
import ListFilterToolbar from '../../../components/dashboard/ListFilterToolbar';
import SuccessSnackbar from '../../../components/dashboard/SuccessSnackbar';
import ErrorState from '../../../components/dashboard/ErrorState';
import { PRESET_PERIODS } from '../../../components/metrics/hooks/useMetricsPeriod';
import { buildLogColumns } from './logColumns';

const PERIOD_DAYS = { '7d': 7, '15d': 15, '30d': 30 };
const RESULTS = { success: true, failure: false };

/**
 * History of one config row, newest first, paged on the server. Hidden on a
 * 403: the log has its own permission (crm.integration_log.view).
 */
export default function IntegrationLogCard({ type, configId }) {
  const { t, i18n } = useTranslation();
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 25 });
  const [result, setResult] = useState('');
  const [period, setPeriod] = useState('');
  const [notice, setNotice] = useState(null);
  const [flashMessage, setFlashMessage] = useState('');

  // Recomputed only when the period changes, so the query key stays stable.
  const since = useMemo(
    () => (period ? new Date(Date.now() - PERIOD_DAYS[period] * 24 * 60 * 60 * 1000).toISOString() : undefined),
    [period],
  );
  const params = {
    page: paginationModel.page + 1,
    pageSize: paginationModel.pageSize,
    ...(result && { success: RESULTS[result] }),
    ...(since && { created_at__gte: since }),
  };
  const logsQuery = useIntegrationLogs(type, configId, params);

  const retry = useRetryIntegrationLog(type, configId, {
    onSuccess: (data) => {
      setNotice(null);
      // The replayed export can fail again: it comes back as a new failed attempt.
      if (type === 'crm' && !data.success) {
        setNotice({ severity: 'warning', message: t('integrations.log.retryFailed') });
        return;
      }
      setFlashMessage(t(type === 'crm' ? 'integrations.log.retrySuccess' : 'integrations.log.syncRequested'));
    },
    onError: (error) => {
      const status = error?.response?.status;
      // 409: nothing to replay (exported since, row disabled...): the backend says why.
      if (status === 409) {
        setNotice({ severity: 'warning', message: error.response.data?.detail ?? t('integrations.log.retryError') });
        return;
      }
      setNotice({ severity: 'error', message: t(status === 403 ? 'integrations.form.errors.forbidden' : 'integrations.log.retryError') });
    },
  });

  const columns = useMemo(
    () => buildLogColumns({ type, t, language: i18n.language, onRetry: retry.mutate }),
    // retry.mutate is stable (TanStack Query keeps it across renders).
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, t, i18n.language],
  );

  if (logsQuery.error?.response?.status === 403) return null;

  const resetPage = () => setPaginationModel((prev) => ({ ...prev, page: 0 }));
  const updateResult = (value) => {
    setResult(value);
    resetPage();
  };
  const updatePeriod = (value) => {
    setPeriod(value);
    resetPage();
  };

  const chips = [];
  if (result) {
    chips.push({
      key: 'result',
      label: `${t('integrations.log.filters.result')}: ${t(`integrations.log.${result}`)}`,
      onDelete: () => updateResult(''),
    });
  }
  if (period) {
    chips.push({
      key: 'period',
      label: `${t('integrations.log.filters.period')}: ${t(`metrics.period.${period}`)}`,
      onDelete: () => updatePeriod(''),
    });
  }
  const count = logsQuery.data?.count ?? 0;

  // Plain props object: `slots.toolbar` stays the imported component, so the toolbar never remounts.
  const toolbarProps = {
    showGridControls: false,
    filters: [
      {
        field: 'result',
        label: t('integrations.log.filters.result'),
        value: result,
        onChange: updateResult,
        options: Object.keys(RESULTS).map((value) => ({ value, label: t(`integrations.log.${value}`) })),
      },
      {
        field: 'period',
        label: t('integrations.log.filters.period'),
        value: period,
        onChange: updatePeriod,
        options: PRESET_PERIODS.map((value) => ({ value, label: t(`metrics.period.${value}`) })),
      },
    ],
    chips,
    resultsLabel: t('integrations.log.resultsFound', { count }),
    clearLabel: t('integrations.filters.clear'),
    onClearAll: () => {
      updateResult('');
      updatePeriod('');
    },
  };

  return (
    <Stack spacing={2} component="section">
      <Stack spacing={0.5}>
        <Typography variant="h6" component="h2">
          {t('integrations.log.title')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`integrations.log.hint.${type}`)}
        </Typography>
      </Stack>
      {notice && (
        <Alert severity={notice.severity} onClose={() => setNotice(null)}>
          {notice.message}
        </Alert>
      )}
      {logsQuery.isError ? (
        <ErrorState
          title={t('integrations.states.errorTitle')}
          message={t('integrations.log.loadError')}
          actionLabel={t('integrations.states.retry')}
          onRetry={logsQuery.refetch}
        />
      ) : (
        // Fixed height: the grid scrolls inside, below the other cards of the page.
        <DataGridCard sx={{ flex: '0 0 auto', height: 560 }}>
          <DataGrid
            rows={logsQuery.data?.results ?? []}
            rowCount={count}
            columns={columns}
            loading={logsQuery.isFetching}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[10, 25, 50]}
            disableRowSelectionOnClick
            slots={{ toolbar: ListFilterToolbar }}
            slotProps={{ toolbar: toolbarProps }}
            localeText={{ noRowsLabel: t('integrations.log.empty') }}
            sx={{ border: 0 }}
          />
        </DataGridCard>
      )}
      <SuccessSnackbar open={Boolean(flashMessage)} message={flashMessage} onClose={() => setFlashMessage('')} />
    </Stack>
  );
}
